import { useEffect, useState } from 'react'

import classNames from 'classnames'
import { connect, DataItem } from 'scrivito'

import { MessageAttachmentPreviewBox } from './MessageAttachmentPreviewBox'
import { MessageAttachment } from './types'
import { Badge } from '@/Atomic/Atoms/Badge'
import { Icon } from '@/Atomic/Atoms/Icon'
import { Avatar } from '@/Atomic/Molecules/Avatar'
import { Contact } from '@/Data/Contact/ContactDataClass'
import { DataBinary, dataBinaryToUrl } from '@/utils/dataBinaryToUrl'
import { formatDateTime } from '@/utils/formatDateTime'

interface MessageListItemProps {
  message: DataItem
  loggedInUserId: string
  onPreviewAttachment: (attachment: MessageAttachment) => void
}

export const MessageListItem: React.FC<MessageListItemProps> = connect(
  ({ message, loggedInUserId, onPreviewAttachment }) => {
    const messageSenderId = message.get('createdBy')
    const isLoggedInUser =
      typeof messageSenderId === 'string' && messageSenderId === loggedInUserId
    const isPublic = message.get('public')

    let messageSenderName: string | undefined
    let messageSenderImg: DataBinary | undefined
    let isExternal: boolean | undefined

    if (typeof messageSenderId === 'string') {
      const sender = Contact.get(messageSenderId)
      messageSenderName = sender?.get('name') as string | undefined
      messageSenderImg = sender?.get('imageThumbnail') as DataBinary | undefined
      isExternal = sender?.get('isExternal') as boolean | undefined
    }

    const [attachments, setAttachments] = useState<MessageAttachment[]>()
    const [downloadURLs, setDownloadURLs] = useState<{ [id: string]: string }>()

    useEffect(() => {
      const fetched = message.get('attachments') as MessageAttachment[] | null
      setAttachments(fetched ?? undefined)

      if (fetched) {
        fetched.forEach(async (attachment) => {
          const res = await dataBinaryToUrl(attachment)
          setDownloadURLs((prev) => ({ ...prev, [attachment._id]: res.url }))
        })
      }
    }, [message])

    return (
      <div
        className={classNames('message-tab__item', {
          'message-tab__item--self': isLoggedInUser,
          'message-tab__item--other': !isLoggedInUser,
        })}
      >
        <div className="message-tab__item-avatar" title={messageSenderName}>
          <Avatar avatar={messageSenderImg} icon="user" />
          {isExternal === false && <Badge value="Staff" variant="info" />}
        </div>
        <div className="message-tab__item-bubble">
          <p className="message-tab__item-text">
            {message.get('text') as string}
          </p>

          {!!attachments?.length && (
            <div className="message-tab__item-attachments">
              {attachments.map((attachment) => (
                <MessageAttachmentPreviewBox
                  key={attachment._id}
                  messageAttachment={attachment}
                  onClick={onPreviewAttachment}
                  downloadUrl={downloadURLs?.[attachment._id]}
                />
              ))}
            </div>
          )}

          <div className="message-tab__item-footer">
            <span className="message-tab__item-timestamp">
              <Icon icon="clock" size="sm" />
              {formatDateTime(message.get('createdAt'))}
            </span>
            {!isPublic && <Badge value="Private" variant="danger" />}
          </div>
        </div>
      </div>
    )
  },
)
